// Concrete ChessMenuContext: wires settings and the game scene into the small
// surface the menu screens consume, so screens never see the scene.
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { Settings } from '../settings/types.js';
import type { ChessMenuContext, LocalOpponent, OnlineMenuApi, Scene } from '../ui/types.js';
import type { GameScene } from './GameScene.js';

export interface MenuContextDeps {
  readonly settings: SettingsManager;
  readonly gameScene: GameScene;
  readonly navigate: (scene: Scene) => void;
  readonly onSettingsChanged: (before: Settings, after: Settings) => void;
  /** Online rooms — `scenes/OnlineMenuHost.ts` owns the room and the handoff. */
  readonly online: OnlineMenuApi;
  readonly openReplay: ChessMenuContext['openReplay'];
}

export function buildMenuContext(deps: MenuContextDeps): ChessMenuContext {
  return {
    navigate: deps.navigate,
    online: deps.online,
    openReplay: deps.openReplay,
    getSettings: () => deps.settings.get(),
    updateSettings: (partial) => {
      const before = deps.settings.get();
      deps.settings.update(partial);
      deps.onSettingsChanged(before, deps.settings.get());
    },
    startLocalGame: (opponent: LocalOpponent, start) => {
      // Show the canvas first so the scene's first frame lands on a visible surface.
      deps.navigate('game');
      deps.gameScene.mount(start === undefined ? { kind: 'local', opponent } : { kind: 'local', opponent, start });
    },
  };
}
